import { MeasuredWoodStackResult, presentWoodStackResult } from "@foresting-tomorrow/forest_tools_inventory";
import {
  getSpeciesArrayStackSolidVolumePercentage,
  getLogLengthPercentage,
  getDiameterBasedCompensationPercentage,
} from "@foresting-tomorrow/forest_tools_inventory";

// material ui
import { Alert, Grid, Typography, Divider, Stack } from "@mui/material";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixtyRounded";

// project import
import Selector from "components/select/selector";
import TextFieldInput from "components/input/textField";
import { useTranslation } from "utils/locales/utilityFunctions";
import { beautifyNumber } from "utils/numberModification";
import { FirestoreInventoryItemProps } from "types/auth";
import { RawWoodSpeciesItem } from "./rawWoodInputInformationSpecies";
import useSpeciesData from "hooks/useSpeciesData";
import { RawWoodOptionProps } from "./options";
import SmallIconButton from "components/buttons/smallIconButton";

interface RawWoodInputConversionFactorProps {
  variant: FirestoreInventoryItemProps["inventoryType"];
  disableInputs: boolean,
  buyerApprovedDisable: boolean,
  stackingOptions: RawWoodOptionProps[];
  straightnessOptions: RawWoodOptionProps[];
  processingOptions: RawWoodOptionProps[];
  snowIceOptions: RawWoodOptionProps[];
  residuesOptions: RawWoodOptionProps[];
  rejectsOptions: RawWoodOptionProps[];
  battensOptions: RawWoodOptionProps[];
  stackingValue: string;
  straightnessValue: string;
  processingValue: string;
  snowIceValue: string;
  residuesValue: string;
  rejectsValue: string;
  battensValue: string;
  inputModeBattens: "count" | "volume";
  inputModeRejects: "count" | "volume";
  species: RawWoodSpeciesItem[];
  averageDiameter: string;
  averageLogLength: string;
  averageStackLength: string;
  averageStackHeight: string;
  woodStackResult: MeasuredWoodStackResult | null;
  setStackingValue: (value: string) => void;
  setStraightnessValue: (value: string) => void;
  setProcessingValue: (value: string) => void;
  setSnowIceValue: (value: string) => void;
  setResiduesValue: (value: string) => void;
  handleSetBattensAndRejectsValue: (
    value: string,
    mode: "battens" | "rejects",
    inputMode: "count" | "volume"
  ) => void;
  setInputModeBattens: (mode: "count" | "volume") => void;
  setInputModeRejects: (mode: "count" | "volume") => void;
}

const RawWoodInputConversionFactor = ({
  variant,
  disableInputs,
  buyerApprovedDisable,
  stackingOptions,
  straightnessOptions,
  processingOptions,
  snowIceOptions,
  residuesOptions,
  rejectsOptions,
  battensOptions,
  stackingValue,
  straightnessValue,
  processingValue,
  snowIceValue,
  residuesValue,
  rejectsValue,
  battensValue,
  inputModeBattens,
  inputModeRejects,
  species,
  averageDiameter,
  averageLogLength,
  averageStackLength,
  averageStackHeight,
  woodStackResult,
  setStackingValue,
  setStraightnessValue,
  setProcessingValue,
  setSnowIceValue,
  setResiduesValue,
  handleSetBattensAndRejectsValue,
  setInputModeBattens,
  setInputModeRejects,
}: RawWoodInputConversionFactorProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { getSpeciesType } = useSpeciesData();

  // --- Derived States --- //
  const selectedStacking = stackingOptions.find((o) => o.id === stackingValue);
  const selectedStraightness = straightnessOptions.find((o) => o.id === straightnessValue);
  const selectedProcessing = processingOptions.find((o) => o.id === processingValue);
  const selectedSnowIce = snowIceOptions.find((o) => o.id === snowIceValue);
  const selectedResidues = residuesOptions.find((o) => o.id === residuesValue);

  // const [inputModeBattens, setInputModeBattens] = useState<'count' | 'volume'>('count');
  // const [inputModeRejects, setInputModeRejects] = useState<'count' | 'volume'>('count');

  const spcArray = species
    ? species.map((s) => {
      const mix = s.mix ? parseFloat(s.mix.replace(",", ".")) : 100;
      return { species: s.id, mix };
    })
    : [];
  const speciesPercentage =
    spcArray.length > 0 ? getSpeciesArrayStackSolidVolumePercentage(spcArray) : undefined;

  const averageLogDiameterNumber: number = isNaN(parseFloat(averageDiameter.replace(",", ".")))
    ? 0
    : parseFloat(averageDiameter.replace(",", "."));

  const logDiameterPercentage =
    averageLogDiameterNumber > 0
      ? getDiameterBasedCompensationPercentage(averageLogDiameterNumber)
      : undefined;

  const averageLogLengthNumber = Number(averageLogLength.replace(",", "."));
  const stackHeightPercentage = woodStackResult?.geometry.stackHeightPercentage;
  const speciesType = species.length > 0 ? getSpeciesType(species[0].id) : "";
  const logLengthPercentage = ["needle", "foliage"].includes(speciesType)
    ? getLogLengthPercentage(averageLogLengthNumber, speciesType as "foliage" | "needle")
    : undefined;

  const result = woodStackResult ? presentWoodStackResult(woodStackResult).totals : {
    compensationFactorPercentage: 0, stackedVolumeM3: 0, volumeM3: 0, greenMassTon: 0,
    numberOfSupportLogs: 0, numberOfCullLogs: 0, volumeOfSupportLogsM3: 0, volumeOfCullLogsM3: 0,
  };

  // --- Functions --- //
  const handleSetInputModeBattens = () => {
    setInputModeBattens(inputModeBattens === "count" ? "volume" : "count");
    // Reset value
    handleSetBattensAndRejectsValue("", "battens", inputModeBattens);
  };
  const handleSetInputModeRejects = () => {
    setInputModeRejects(inputModeRejects === "count" ? "volume" : "count");
    // Reset value
    handleSetBattensAndRejectsValue("", "rejects", inputModeRejects);
  };
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-input-conversion-factor-structure")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-base")}
          value={speciesPercentage ? beautifyNumber(speciesPercentage, 0) : ""}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"%"}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-mean-diameter")}
          value={averageDiameter}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"cm"}
        />

        {logDiameterPercentage !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}
            {logDiameterPercentage > 0 ? "+" : ""}
            {logDiameterPercentage}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <Selector
          title={translate("input-dialog-raw-wood-input-conversion-factor-stacking")}
          value={stackingValue}
          options={stackingOptions}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => setStackingValue(e.target.value)}
        />

        {selectedStacking?.adjustment !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}{" "}
            {selectedStacking.adjustment}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <Selector
          title={translate("input-dialog-raw-wood-input-conversion-factor-straightness")}
          value={straightnessValue}
          options={straightnessOptions}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => setStraightnessValue(e.target.value)}
        />

        {selectedStraightness?.adjustment !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}{" "}
            {selectedStraightness.adjustment}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <Selector
          title={translate("input-dialog-raw-wood-input-conversion-factor-processing")}
          value={processingValue}
          options={processingOptions}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => setProcessingValue(e.target.value)}
        />

        {selectedProcessing?.adjustment !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}{" "}
            {selectedProcessing.adjustment}
          </Typography>
        )}
      </Grid>

      {/* <Grid item xs={12}>
        <Divider sx={{ mt: 1, mb: 1 }} />
      </Grid> */}

      {/* <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-input-conversion-factor-deviations")}
        </Typography>
      </Grid> */}

      <Grid item xs={12} sm={12 / 5}>
        <Selector
          title={translate("input-dialog-raw-wood-input-conversion-factor-snow-ice")}
          value={snowIceValue}
          options={snowIceOptions}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => setSnowIceValue(e.target.value)}
        />

        {selectedSnowIce?.adjustment !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}{" "}
            {selectedSnowIce.adjustment}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <Selector
          title={translate("input-dialog-raw-wood-input-conversion-factor-residues")}
          value={residuesValue}
          options={residuesOptions}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => setResiduesValue(e.target.value)}
        />

        {selectedResidues?.adjustment !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}{" "}
            {selectedResidues.adjustment}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5} >
        <Stack
          direction={"row"}
          spacing={0}
          sx={{ alignItems: "center", bgcolor: "secondary.lighter", borderRadius: 1.5 }}
        >
          {inputModeRejects === "count" && (
            <Selector
              title={translate("input-dialog-raw-wood-input-conversion-factor-rejects")}
              value={rejectsValue}
              options={rejectsOptions}
              label={true}
              fullWidth={true}
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) =>
                handleSetBattensAndRejectsValue(e.target.value, "rejects", inputModeRejects)
              }
            />
          )}
          {inputModeRejects === "volume" && (
            <TextFieldInput
              title={translate("input-dialog-raw-wood-input-conversion-factor-rejects")}
              value={rejectsValue}
              showLabel={true}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
              inputAdornment={"m3"}
              handleChange={(e) =>
                handleSetBattensAndRejectsValue(e.target.value, "rejects", inputModeRejects)
              }
            />
          )}
          <SmallIconButton
            tooltipText={translate("input-dialog-raw-wood-mode-tooltip")}
            status="enabled"
            onClick={handleSetInputModeRejects}
            orientation="horizontal"
            disabled={disableInputs || buyerApprovedDisable}
            sx={{ height: 41 }}
          >
            <ThreeSixtyIcon
              sx={{ transform: inputModeRejects === "count" ? "rotate(180deg)" : "rotate(360deg)" }}
            />
          </SmallIconButton>
        </Stack>
        {inputModeRejects === "count" && result.volumeOfCullLogsM3 !== 0 && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {beautifyNumber(result.volumeOfCullLogsM3, 2) + " m3"}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <Stack
          direction={"row"}
          spacing={0}
          sx={{ alignItems: "center", bgcolor: "secondary.lighter", borderRadius: 1.5 }}
        >
          {inputModeBattens === "count" && (
            <Selector
              title={translate("input-dialog-raw-wood-input-conversion-factor-battens")}
              value={battensValue}
              options={battensOptions}
              label={true}
              fullWidth={true}
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) =>
                handleSetBattensAndRejectsValue(e.target.value, "battens", inputModeBattens)
              }
            />
          )}
          {inputModeBattens === "volume" && (
            <TextFieldInput
              title={translate("input-dialog-raw-wood-input-conversion-factor-battens")}
              value={battensValue}
              showLabel={true}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
              inputAdornment={"m3"}
              handleChange={(e) =>
                handleSetBattensAndRejectsValue(e.target.value, "battens", inputModeBattens)
              }
            />
          )}
          <SmallIconButton
            tooltipText={translate("input-dialog-raw-wood-mode-tooltip")}
            status="enabled"
            onClick={handleSetInputModeBattens}
            orientation="horizontal"
            disabled={disableInputs || buyerApprovedDisable}
            sx={{ height: 41 }}
          >
            <ThreeSixtyIcon
              sx={{ transform: inputModeBattens === "count" ? "rotate(180deg)" : "rotate(360deg)" }}
            />
          </SmallIconButton>
        </Stack>
        {inputModeBattens === "count" && result.volumeOfSupportLogsM3 !== 0 && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {beautifyNumber(result.volumeOfSupportLogsM3, 2) + " m3"}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-input-conversion-factor-measurement")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-stack-height")}
          value={averageStackHeight}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"m"}
        />

        {stackHeightPercentage !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}
            {stackHeightPercentage > 0 ? "+" : ""}
            {stackHeightPercentage}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-log-length")}
          value={averageLogLength}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"m"}
        />

        {logLengthPercentage !== undefined && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("input-dialog-raw-wood-input-conversion-factor-correction")}
            {logLengthPercentage > 0 ? "+" : ""}
            {logLengthPercentage}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-input-conversion-factor-calculations")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-result")}
          value={beautifyNumber(result.compensationFactorPercentage, 2)}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"%"}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-cubic-metres")}
          value={beautifyNumber(result.stackedVolumeM3, 2)}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={translate("raw-wood-input-rm")}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-volume")}
          value={beautifyNumber(result.volumeM3, 2)}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"m3"}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 5}>
        <TextFieldInput
          title={translate("input-dialog-raw-wood-input-conversion-factor-weight")}
          value={beautifyNumber(result.greenMassTon, 2)}
          showLabel={true}
          fullWidth={true}
          disabled={true}
          inputAdornment={"ton"}
        />
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          {translate("input-dialog-raw-wood-input-conversion-factor-rounding-notice")}
        </Alert>
      </Grid>
    </>
  );
};

export default RawWoodInputConversionFactor;
